import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, TextField } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import DropdownSelect from '../../components/dropdown-select/DropdownSelect'
import { HeaderBlock } from '../../components/header-block/HeaderBlock'
import { Loading } from '../../components/loading/Loading'
import PageContainer from '../../components/page-container/PageContainer'
import Search from '../../components/search/Search'
import RecursiveTreeView from '../../components/tree-view/TreeView'
import { DeepCopy } from '../../helpers/DeepCopy'
import { createNode, getHierarchy, setHeirarchyExpansion } from '../../state/actions/HierarchyActions'
import { generateRoutes } from '../../state/actions/RouteActions'

function HierarchiesPage(props) {

    const hierarchy = useSelector(state => state.HierarchyReducer.hierarchy)
    const instanceId = useSelector(state => state.RouteReducer.instanceId)
    const statusOutput = useSelector(state => state.RouteReducer.statusOutput)
    const hierarchyExpansion = useSelector(state => state.HierarchyReducer.hierarchyExpansion)

    const [t, i18n] = useTranslation()
    const dispatch = useDispatch()

    const [loading, setLoading] = useState(false)
    const [newNodeModalOpen, setNewNodeModalOpen] = useState(false)
    const [createLeafNodesModalOpen, setCreateLeafNodesModalOpen] = useState(false)
    const [nodeParents, setNodeParents] = useState([])
    const [nodeChildren, setNodeChildren] = useState([])
    const [nodeDisplayName, setNodeDisplayName] = useState(null)
    const [displayNameError, setDisplayNameError] = useState(null)
    const [searchedHierarchyTree, setSearchedHierarchyTree] = useState([])
    const [hierarchyTree, setHierarchyTree] = useState([])
    const [newData, setNewData] = useState(true)
    const [generatingRoutes, setGeneratingRoutes] = useState(false)
    const [syncFailed, setSyncFailed] = useState(false)

    const [searchText, setSearchText] = useState("")

    useEffect(() => {
        if (newData) {
            setLoading(true)
            dispatch(getHierarchy(() => {
                setLoading(false)
                setNewData(false)
            }))
        }
    }, [newData])

    useEffect(() => {
        createTree()
    }, [hierarchy])

    useEffect(() => {
        if (instanceId == null) {
            setGeneratingRoutes(false)
        } else {
            setGeneratingRoutes(true)
        }
    }, [instanceId])

    useEffect(() => {
        if (statusOutput == "Failed" || statusOutput == "Cancelled" || statusOutput == "Terminated") {
            setGeneratingRoutes(false)
            setSyncFailed(true)
        } else {
            setSyncFailed(false)
        }
    }, [statusOutput])


    function handleStartSync() {
        setCreateLeafNodesModalOpen(false)
        dispatch(generateRoutes(!syncFailed ? instanceId : null, () => {
            setSyncFailed(false)
        }))
    }

    function handleSearch(value) {
        if (value == "") {
            setSearchedHierarchyTree(hierarchyTree)
            return;
        }

        const newList = []
        // Can have multiple root nodes, check them all
        hierarchyTree.forEach(root => {
            var stack = [], node, ii;
            stack.push(root);

            // Loop through stack, popping off the latest addition to inspect
            while (stack.length > 0) {
                node = stack.pop()

                const displayName = (node?.DisplayName || "").toLowerCase()
                const id = (node?.id || "").toLowerCase()
                const lowercaseValue = value.toLowerCase()

                // Check current node, does it match search params?
                if (displayName.includes(lowercaseValue) || id.includes(lowercaseValue)) {
                    // Yes, add to list
                    newList.push(node)
                }

                // Continue searching the children of the node, push all to stack
                if (node.Children && node.Children.length) {
                    for (ii = 0; ii < node.Children.length; ii += 1) {
                        stack.push(node.Children[ii])
                    }
                }
            }
        });

        setSearchedHierarchyTree(newList)
    }

    function createTree() {
        // make copy
        const localHierarchy = DeepCopy(hierarchy);

        var treeList = [];

        Object.values(localHierarchy).forEach(obj => {

            if (obj.Parents == null || obj.Parents.length == 0) {
                treeList.push(obj);

            } else {
                var parentPresent = false
                obj.Parents.forEach(parent => {
                    if (localHierarchy[parent] != null) {
                        parentPresent = true
                    }
                })

                if (!parentPresent) {
                    treeList.push(obj);
                } else {
                    obj.Parents.forEach((parent) => {
                        if (localHierarchy[parent] != null) {
                            if (localHierarchy[parent].Children == null) {
                                localHierarchy[parent].Children = []
                            }

                            localHierarchy[parent].Children.push(obj);
                        }
                    })
                }
            }
        });

        setHierarchyTree(treeList)
        setSearchedHierarchyTree(treeList)
    }

    function nodeOnClick(nodeId) {
        props.history.push(`/hierarchies/${nodeId}`)
    }

    function setExpandedArray(dataArray) {
        dispatch(setHeirarchyExpansion(dataArray))
    }

    function renderContent() {
        return (
            <RecursiveTreeView
                data={searchedHierarchyTree}
                nodeOnClick={nodeOnClick}
                nameField="DisplayName"
                secondaryField="id"
                childrenField="Children"
                expandedArray={hierarchyExpansion || []}
                setExpandedArray={setExpandedArray}
                flatTree={Object.values(DeepCopy(hierarchy))}
            />
        )
    }

    function addNode() {
        if (nodeDisplayName == "" || nodeDisplayName == null) {
            setDisplayNameError(true)
        } else {
            const node = {
                DisplayName: nodeDisplayName,
                ParentIds: nodeParents,
                ChildIds: nodeChildren
            }

            dispatch(createNode(node, (success) => {
                if (success) {
                    setNewNodeModalOpen(false)
                    setNewData(true)
                }
            }))
        }
    }

    function newNodeModal() {
        return (
            <Dialog
                open={newNodeModalOpen}
                fullWidth={true}
            >
                <DialogTitle>
                    {t('hierarchy.add-node')}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                margin="dense"
                                variant="outlined"
                                fullWidth
                                onChange={event => {
                                    if (displayNameError === true) {
                                        setDisplayNameError(false)
                                    }

                                    setNodeDisplayName(event.target.value)
                                }}
                                label={t('hierarchy.display-name')}
                                error={displayNameError}
                                helperText={displayNameError ? t('hierarchy.name-required') : null}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <DropdownSelect
                                value={nodeParents}
                                label={t('hierarchy.parent')}
                                handleChange={(value) => {
                                    // ParentIds is currently expected to be a list
                                    if (value != null && value != "") {
                                        setNodeParents([value])
                                    } else {
                                        setNodeParents([])
                                    }
                                }}
                                data={Object.values(hierarchy).map(item => ({ key: item.id, value: item.DisplayName }))}
                                disabledData={nodeChildren}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => setNewNodeModalOpen(false)}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={addNode}
                    >
                        {t('common.submit')}
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    function leafNodeModal() {
        return (
            <Dialog
                open={createLeafNodesModalOpen}
                fullWidth={true}>
                <DialogTitle>
                    {t('hierarchy.generate-all-route-nodes')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('hierarchy.generate-nodes-description')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined"
                        color="secondary"
                        onClick={() => {
                            setCreateLeafNodesModalOpen(false)
                        }}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleStartSync}
                    >
                        {t('hierarchy.start-sync')}
                    </Button>
                </DialogActions>
            </Dialog >
        )
    }

    return (
        <Box>
            <HeaderBlock
                title={t('hierarchy.hierarchies')}
                right={
                    <Grid container spacing={2} alignItems="center" justify="flex-end">
                        <Grid item>
                            {statusOutput != null ? <Chip label={`Last Sync Status: ${statusOutput}`} /> : null}
                        </Grid>
                        <Grid item>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() => generatingRoutes ? handleStartSync() : setCreateLeafNodesModalOpen(true)}
                            >
                                {generatingRoutes ? t('common.check-sync-status') : t('hierarchy.sync-routes-nodes')}
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                onClick={() => setNewNodeModalOpen(true)}
                                variant="outlined"
                            >
                                {t('hierarchy.add-node')}
                            </Button>
                        </Grid>
                        <Grid item>
                            <Search
                                searchTitle={t("hierarchy.search-hierarchies")}
                                searchFunction={handleSearch}
                            />
                        </Grid>
                    </Grid>
                }
            />

            {newNodeModal()}
            {leafNodeModal()}

            <PageContainer>
                {loading ? <Loading /> : renderContent()}
            </PageContainer>
        </Box>
    )
}

HierarchiesPage.propTypes = {

}

export default HierarchiesPage

